import type { RegisteredComponent } from "@builder.io/sdk-react";

import { FeatureCards } from "~/hec/components/organisms/feature-cards/feature-cards";
import type { SiteId } from "~/hec/utils/site-id";
import { useRootLayoutData } from "~/root";

import { toFeatureLinkTarget } from "../feature-link/feature-link.logic";
import { featureCardsInputs } from "./inputs";
import type { BuilderFeatureCardsProps } from "./types";

/**
 * Feature Cards (COM-945): replaces the "Telehealth Feature" symbol. Thin
 * wrapper: resolves the card and button links and spreads `attributes` on the
 * root (`noWrap` contract, CLAUDE.md §4).
 */
const BuilderFeatureCards = ({
  heading,
  items = [],
  buttonText,
  attributes,
  ...buttonLink
}: BuilderFeatureCardsProps) => {
  const { site } = useRootLayoutData();
  const siteId = site.id as SiteId;
  return (
    <FeatureCards
      heading={heading}
      items={items.map(item => ({
        desktopImage: item.desktopImage,
        mobileImage: item.mobileImage,
        logo: item.logo,
        logoAlt: item.logoAlt,
        headline: item.headline,
        badge: item.badge?.trim() || undefined,
        blockColor: item.blockColor,
        textColor: item.textColor,
        whiteArrow: item.whiteArrow,
        link: toFeatureLinkTarget(item, siteId),
      }))}
      buttonText={buttonText}
      buttonLink={toFeatureLinkTarget(buttonLink, siteId)}
      {...attributes}
    />
  );
};

export const FeatureCardsDefinition = {
  component: BuilderFeatureCards,
  name: "FeatureCards",
  // Label only — safe to rename. `name` above is the published contract.
  friendlyName: "Feature Cards",
  noWrap: true,
  canHaveChildren: false,
  image: "https://unpkg.com/lucide-static@0.537.0/icons/layout-grid.svg",
  inputs: featureCardsInputs,
} satisfies RegisteredComponent;

export default BuilderFeatureCards;
